import { Component, Input, SimpleChanges, Output, EventEmitter } from '@angular/core';
import {
  ColGroupDef,
  ColDef,
  GridApi,
  IServerSideDatasource,
  GetRowIdParams,
  GetRowIdFunc,
  IServerSideGetRowsParams,
} from 'ag-grid-community';
import { Person, SearchPersonsData200Response, SearchFilter } from 'ldt-people-api';
import { AuthService } from 'src/app/auth/service/auth.service';
import {
  PositionStatus,
  QuickBuild,
  QuickBuildService,
  UIFilter,
  UIFilterGroup,
} from 'src/app/people/quick-build.service';
import { DarkModeService } from '../../dark-mode/dark-mode.service';
import { GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { Log } from 'src/app/logger.service';
import { StreamsSubscribeDrawerService } from '../../streams-subscribe-drawer/streams-subscribe-drawer.service';
import { MatDialog } from '@angular/material/dialog';
import { PersonProfileComponent } from '../../person-profile/person-profile.component';
import { JobCellComponent } from '../../ag-grid-cell-renderers/job-cell.component';
import { PeopleDataResponse, PeopleReportService } from 'src/app/people/report-service.service';
import { PeopleGridConfig } from '../interfaces/report-config.interfaces';

@Component({
  selector: 'app-reports-people-grid',
  templateUrl: './people-grid.component.html',
  styleUrls: ['./people-grid.component.scss'],
  standalone: false,
})
export class PeopleGridComponent {
  @Input() quickBuild: QuickBuild | null = null;
  @Input() boxShadow: boolean = true;
  @Output() configChange = new EventEmitter<PeopleGridConfig>();

  peopleGridType: 'sample' | 'full' = 'sample';

  // Configuration management
  private _movementType: 'hires' | 'departures' = 'hires';
  private _hideNone: boolean = false;

  movementTypeOptions = [
    { value: 'hires', displayText: 'Hires' },
    { value: 'departures', displayText: 'Departures' },
  ];

  @Input()
  set movementType(value: 'hires' | 'departures' | undefined) {
    if (value) {
      this._movementType = value;
      this.emitConfiguration();
    }
  }
  get movementType(): 'hires' | 'departures' {
    return this._movementType;
  }

  @Input()
  set hideNone(value: boolean | undefined) {
    if (value !== undefined) {
      this._hideNone = value;
      this.emitConfiguration();
    }
  }
  get hideNone(): boolean {
    return this._hideNone;
  }

  /// AG -GRID --------------
  rowData: Person[];
  rowModelType: string = 'serverSide';
  serverSideDatasource: IServerSideDatasource = this.dwDatasource();
  overlayNoRowsTemplate: string = 'No movement found in the last 90 days';
  defaultColDef: ColDef = {
    sortable: false,
    floatingFilter: false,
    resizable: true,
    flex: 1,
    minWidth: 100,
    enablePivot: false,
    autoHeight: true,
    cellClass: 'cell-wrap-text',
  };

  columnDefs: (ColDef | ColGroupDef)[] = peoplePreviewGridSettings;
  public getRowId: GetRowIdFunc = (params: GetRowIdParams) => params.data.id;

  paginationToken: string | undefined;
  countUpdating: boolean = true;
  viewCount = 0;
  refreshing: boolean = true;

  constructor(
    private authService: AuthService,
    public darkModeService: DarkModeService,
    private streamsDrawerService: StreamsSubscribeDrawerService,
    public dialog: MatDialog,
    private reportService: PeopleReportService
  ) {
    const peopleSettings = this.authService.getSelectedOrgValue?.settings?.people;
    if (peopleSettings?.subType === 'unlimited') {
      this.peopleGridType = 'full';
    }

    // Emit initial configuration
    this.emitConfiguration();
  }

  ngOnChanges(changes: SimpleChanges) {
    Log.d('PeopleGridComponent', 'ngOnChanges', changes);
    this.refreshing = true;
    Log.d('PeopleGridComponent', 'ngOnChanges gridApi', this.gridApi);
    this.gridApi?.refreshServerSide({ purge: true });
  }

  gridApi: GridApi<any>;
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
  }

  onRowClicked(event: RowClickedEvent) {
    if (event.data) {
      const currentIndex = this.gridApi.getRowNode(event.data.id)?.rowIndex || 0;
      const rowData: Person[] = [];
      this.gridApi.forEachNode((node) => {
        if (node.data) {
          rowData.push(node.data);
        }
      });

      this.dialog.open(PersonProfileComponent, {
        backdropClass: 'darker-dialog-backdrop',
        width: '100%',
        height: '100%',
        panelClass: 'person-profile-dialog',
        data: {
          person: event.data,
          people: rowData,
          currentIndex: currentIndex,
        },
      });
    }
  }

  onMovementTypeChanged(value: string) {
    this._movementType = value as 'hires' | 'departures';
    this.refreshing = true;
    this.gridApi?.refreshServerSide({ purge: true });
    this.emitConfiguration();
  }

  dwDatasource() {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        Log.d('PeopleGridComponent', 'getRows', params);
        params.api.hideOverlay();

        if (!this.quickBuild) {
          params.success({
            rowData: [],
          });
          this.viewCount = 0;
          this.refreshing = false;
          this.countUpdating = false;
          this.paginationToken = undefined;
          return;
        }

        const size = (params.request.endRow || 0) - (params.request.startRow || 0);

        // If the grid is back at the top, reset the pagination token
        if (params.request.startRow === 0) {
          delete this.paginationToken;
          this.countUpdating = true;
        }

        let sortBy = '-position.started_at';
        let currentQuickBuild = this.quickBuild;
        if (this.movementType === 'departures') {
          sortBy = '-jobs.ended_at';
          currentQuickBuild = this.getQuickBuildForType('departures');
        } else {
          currentQuickBuild = this.getQuickBuildForType('hires');
        }

        this.reportService
          .getPeopleData(
            currentQuickBuild,
            this.peopleGridType === 'sample',
            sortBy,
            size,
            this.paginationToken
          )
          .subscribe({
            next: (res: PeopleDataResponse) => {
              let people = res?.people || [];
              if (this.hideNone) {
                people = people.filter(
                  (p) =>
                    p.jobs &&
                    p.jobs.length >= 2 &&
                    p.jobs[0].company?.name !== 'None' &&
                    p.jobs[1].company?.name !== 'None'
                );
              }

              params.success({
                rowData: people,
              });
              if ('pagination_token' in res) {
                this.paginationToken =
                  (res as SearchPersonsData200Response).pagination_token || undefined;
              } else {
                this.paginationToken = undefined;
              }

              if (res.count === 0) {
                params.api.showNoRowsOverlay();
              }

              this.viewCount = res.count || 0;
              this.refreshing = false;
              this.countUpdating = false;
            },
            error: (error: any) => {
              params.fail();
              this.viewCount = 0;
              this.refreshing = false;
              this.countUpdating = false;
            },
          });
      },
    };
  }

  onViewPeopleClicked() {
    this.openDrawer(this.getQuickBuildForType(this.movementType));
  }

  private getQuickBuildForType(type: 'hires' | 'departures'): QuickBuild {
    if (!this.quickBuild) {
      throw new Error('QuickBuild is null');
    }

    const qb = QuickBuildService.copyQuickBuild(this.quickBuild);
    if (type === 'hires') {
      (qb.rootFilterGroup.filters[0] as UIFilterGroup).jobsGroupType = 'active';
      (qb.rootFilterGroup.filters[0] as UIFilterGroup).positionStatus = PositionStatus.First;
      const recentFilter: UIFilter = {
        filter: {
          field: 'jobs.started_at',
          match_type: SearchFilter.MatchTypeEnum.Fuzzy,
          type: SearchFilter.TypeEnum.Must,
          date_from: 'now/M-3M/M',
        },
      };
      QuickBuildService.addFiltersToFilterGroup(qb, [recentFilter], 'moneyball');
    } else {
      (qb.rootFilterGroup.filters[0] as UIFilterGroup).jobsGroupType = 'ended';
      (qb.rootFilterGroup.filters[0] as UIFilterGroup).positionStatus = PositionStatus.Last;
      const recentFilter: UIFilter = {
        filter: {
          field: 'jobs.ended_at',
          match_type: SearchFilter.MatchTypeEnum.Fuzzy,
          type: SearchFilter.TypeEnum.Must,
          date_from: 'now/M-3M/M',
        },
      };
      QuickBuildService.addFiltersToFilterGroup(qb, [recentFilter], 'moneyball');
    }
    return qb;
  }

  protected openDrawer(qb: QuickBuild) {
    Log.d('PeopleGridComponent openDrawer', qb);

    this.streamsDrawerService.openDrawer({
      quickBuild: qb,
      title: qb.name,
    });
  }

  private emitConfiguration(): void {
    const config: PeopleGridConfig = {
      movementType: this._movementType,
    };
    this.configChange.emit(config);
  }
}

const peoplePreviewGridSettings: (ColDef | ColGroupDef)[] = [
  {
    headerName: 'Current Job',
    field: 'position',
    cellRenderer: JobCellComponent,
    autoHeight: true,
    flex: 2,
    cellRendererParams: {
      suppressCount: true,
      showEndDate: false,
    },
  },
  {
    headerName: 'Previous Job',
    field: 'jobs',
    valueGetter: (params) => params.data?.jobs?.[1],
    cellRenderer: JobCellComponent,
    autoHeight: true,
    flex: 2,
    cellRendererParams: {
      suppressCount: true,
      showEndDate: true,
    },
  },
];
